import { useState } from "react";
import classes from "./SearchForm.module.css";
import { useDeviceContext } from "src/context/device-context";
import { DesktopSearchForm } from "./DesktopSearchForm";
import { MobileSearchForm } from "./MobileSearchForm";
import { TRIP_TYPE as TRIP } from "src/constants";

import {
  getSelectedLocale,
  markDirectFlightsRequired,
  saveTripSearchToStorage,
} from "src/utils/storage-utils";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { buildSearchQuery, buildBookingUrl } from "src/utils/query-utils";
import isEqual from "lodash.isequal";
import { flightFilterActions } from "src/store/filter-sort";
import store from "src/store";
import { useLocaleContext } from "src/context/locale-context";

function FlightSearchForm() {
  const { getStringRes } = useLocaleContext();
  const location = useLocation();
  const isResultsPage = location.pathname.startsWith("/search/results");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchData = useSelector((state) => state.search, shallowEqual);

  const [prevSearchParams, setPrevSearchParams] = useState({
    locations: searchData.trips.map((tr) => [tr.srcLocation.iata, tr.destLocation.iata]),
    tripType: searchData.tripType,
  });
  const { isMobile } = useDeviceContext();

  const validateForm = (tripType, searchData, onValidateFailCallback) => {
    return validateTrip(tripType, searchData.trips[0], onValidateFailCallback);
  };

  const validateTrip = (tripType, trip, onValidateFailCallback) => {
    let screenId = "";
    if (!trip.srcLocation.city) {
      screenId = "src-location";
    } else if (!trip.destLocation.city) {
      screenId = "dest-location";
    } else if (!trip.startDate) {
      screenId = "start-date";
    } else if (tripType === TRIP.roundtrip && !trip.endDate) {
      screenId = "end-date";
    } else {
      return true;
    }
    onValidateFailCallback(screenId, trip.id);
    return false;
  };

  const cacheSearchData = async (tripType, searchData) => {
    console.log("saving trip");
    searchData.trips.forEach((trip) => {
      saveTripSearchToStorage(trip, tripType);
    });
  };

  const preValidateForm = (tripType, searchData, onValidateFail) => {
    return validateForm(tripType, searchData, onValidateFail ? onValidateFail : () => {});
  };

  const onClickSearchHandler = (
    tripType,
    searchData,
    onValidateFail,
    isDirect,
    checkAccommodation
  ) => {
    if (validateForm(tripType, searchData, onValidateFail)) {
      const tripLocations = searchData.trips.map((tr) => [
        tr.srcLocation.iata,
        tr.destLocation.iata,
      ]);

      if (
        (prevSearchParams.locations && !isEqual(tripLocations, prevSearchParams.locations)) ||
        prevSearchParams.tripType !== tripType
      ) {
        dispatch(flightFilterActions.clearAllFilters());
      }
      setPrevSearchParams({ locations: tripLocations, tripType: tripType });
      cacheSearchData(tripType, searchData);

      const q = buildSearchQuery(searchData);
      const locale = getSelectedLocale();
      const lang = locale.split("_")[0];
      if (isDirect) markDirectFlightsRequired();

      store.dispatch(flightFilterActions.updatePinnedArrivalSegment([]));
      store.dispatch(flightFilterActions.updatePinnedDepartureSegment([]));
      if (isResultsPage || !checkAccommodation) {
        navigate(`/search/results?lang=${lang}&${q}`, { replace: isResultsPage });
      } else {
        // and navigate to results page in new tab
        const resultsPageAddress = `${window.location.protocol}//${window.location.host}/search/results?lang=${lang}&${q}`;
        window.open(resultsPageAddress, "_blank");
      }

      // open booking.com page in background
      if (checkAccommodation) {
        console.log("checking with booking");
        const bookingUrl = buildBookingUrl(
          searchData.trips[0].destLocation.city,
          searchData.trips[0].startDate,
          searchData.trips[0].endDate,
          searchData.passengers.adult,
          searchData.passengers.child,
          searchData.passengers.infant,
          locale,
          searchData.trips[0].destLocation.iata
        );
        console.log(bookingUrl);
        window.location.href = bookingUrl;
      }
      return true;
    }
    return false;
  };

  const props = {
    onSearchStart: onClickSearchHandler,
    isResultsPage: isResultsPage,
    heading: getStringRes("sf.desktop.title"),
    searchType: "flights",
    hideCheckboxes: isResultsPage,
  };

  if (isResultsPage && !isMobile) {
    return <DesktopSearchForm key={"desktop-results"} {...props} />;
  } else {
    return (
      <section
        id={isMobile ? "search-form-section-mob" : "search-form-section"}
        className={`${classes.container} ${classes.search} ${
          isMobile ? "mobile" : classes.desktop
        }`}>
        {isMobile ? (
          <MobileSearchForm
            key={isResultsPage ? "mobile-results" : "mobile-homepage"}
            preValidate={preValidateForm}
            {...props}
          />
        ) : (
          <DesktopSearchForm key={"desktop-homepage"} {...props} />
        )}
      </section>
    );
  }
}

export default FlightSearchForm;
